import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ChatWidget = () => {
  const { lang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const texts = {
    de: { title: 'ICG Assistent', placeholder: 'Frag mich etwas...', greeting: 'Hallo! Ich bin der KI-Assistent der ICG Hannover. Wie kann ich dir helfen? Du kannst mich z.B. fragen: Wann ist der Gottesdienst? Wo findet er statt? Was ist Youth Arise?' },
    en: { title: 'ICG Assistant', placeholder: 'Ask me something...', greeting: 'Hello! I am the AI assistant of ICG Hannover. How can I help you? You can ask me: When is the service? Where is it held? What is Youth Arise?' },
    fr: { title: 'Assistant ICG', placeholder: 'Pose-moi une question...', greeting: 'Bonjour! Je suis l\'assistant IA de l\'ICG Hannover. Comment puis-je t\'aider? Tu peux me demander: Quand est le culte? Ou se trouve-t-il?' },
  };
  const t = texts[lang] || texts.de;

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'assistant', content: t.greeting }]);
    }
  }, [isOpen, t.greeting, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isStreaming) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setIsStreaming(true);

    // Add empty assistant message for streaming
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      const response = await fetch(`${API_BASE}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.session_id && !sessionId) {
                setSessionId(data.session_id);
              }
              if (data.text) {
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, content: last.content + data.text };
                  }
                  return updated;
                });
              }
              if (data.error) {
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: 'Es tut mir leid, ein Fehler ist aufgetreten. Bitte versuche es erneut.' };
                  return updated;
                });
              }
            } catch (err) {
              console.error('Error parsing chat SSE:', err);
            }
          }
        }
      }
    } catch (err) {
      console.error('Chat connection error:', err);
      setMessages(prev => {
        const updated = [...prev];
        if (updated[updated.length - 1]?.role === 'assistant' && !updated[updated.length - 1]?.content) {
          updated[updated.length - 1] = { role: 'assistant', content: 'Verbindungsfehler. Bitte versuche es erneut.' };
        }
        return updated;
      });
    } finally {
      setIsStreaming(false);
    }
  };

  return (
    <>
      {/* Chat Bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="chat-widget-toggle"
          className="fixed bottom-20 lg:bottom-6 right-4 z-50 w-14 h-14 bg-[#0f2440] text-white rounded-full shadow-xl hover:scale-110 transition-all flex items-center justify-center hover:shadow-2xl"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          data-testid="chat-widget-window"
          className="fixed bottom-20 lg:bottom-6 right-4 z-50 w-[340px] sm:w-[380px] h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#0f2440] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm">{t.title}</p>
                <p className="text-[10px] text-white/60">GPT</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chat-widget-close"
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={`${msg.role}-${i}`} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 bg-[#0f2440] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  data-testid={`chat-msg-${i}`}
                  className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#0f2440] text-white rounded-br-md'
                      : 'bg-white text-gray-800 border border-gray-100 shadow-sm rounded-bl-md'
                  }`}
                >
                  {msg.content || (isStreaming && i === messages.length - 1 ? (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  ) : '')}
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 bg-sky-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-100 flex-shrink-0">
            <form
              onSubmit={e => { e.preventDefault(); sendMessage(); }}
              className="flex gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={t.placeholder}
                disabled={isStreaming}
                data-testid="chat-input"
                className="flex-1 px-3 py-2.5 bg-gray-100 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0f2440]/20 focus:border-[#0f2440] focus:bg-white transition-all disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isStreaming || !input.trim()}
                data-testid="chat-send"
                className="w-10 h-10 bg-[#0f2440] text-white rounded-xl flex items-center justify-center hover:bg-[#1a3a5c] transition-colors disabled:opacity-50 flex-shrink-0"
              >
                {isStreaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
